/**
 * Bridge from a resolved cloud connection grant to `TerminalConfig` — the
 * additive layer over the original seam. `withCloudConnection(base, opts)`
 * applies ONLY the cloud-generic connection fields; everything host-specific
 * (accountId, user profile, namespace, catalog, onLogout, onDownloadApp, …)
 * stays in `base` untouched. Hosts that supply the gateway directly simply
 * never call this.
 */
import type { TerminalConfig } from '../config.js';
import type { ConnectionGrant, SidecarAuth } from './api.js';

export interface CloudConnectionOptions {
  /** The in-memory grant from `useDimeConnection`'s 'ready' phase. */
  readonly grant: ConnectionGrant;
  /**
   * Sidecar forward: model catalog + session sync go through the web-api's
   * `/v1/gateway/*` with a DIME JWT instead of hitting the gateway host
   * directly — the browser never needs the gateway URL/token for them.
   */
  readonly sidecar?: SidecarAuth;
  /**
   * Fresh DIME JWT per ws handshake. Applied only for gateway-proxy grants
   * (`grant.usesDimeJwt`) — a fresh JWT is the WRONG credential for a
   * direct-gateway (token) grant, which replays its long-lived token as-is.
   */
  readonly resolveHandshakeToken?: TerminalConfig['resolveHandshakeToken'];
  /**
   * The credential-store calls for the Credentials tab. Cloud-only:
   * they need the DIME web-api, so a host that supplies its own gateway passes
   * nothing and the pane is absent.
   */
  readonly credentials?: TerminalConfig['credentials'];
  /**
   * Fired when the gateway connection reaches a terminal 'error' status
   * (auth rejection, retries exhausted). The host re-resolves the grant with
   * a fresh DIME JWT and remounts — the 5-minute-TTL recovery path
   * (`useDimeConnection().refreshGrant`).
   */
  readonly onConnectionDead: () => void;
}

/**
 * Apply the cloud connection grant on top of a host's base `TerminalConfig`.
 *
 * Differences from a manual-pairing config:
 *  - `ephemeralConnection`: the gateway URL/tokens never touch localStorage —
 *    the grant is re-resolved from the backend every session.
 *  - `supportsPairingRole: false`: this is a token-auth path; on an auth
 *    rejection the client must surface a terminal error (which triggers
 *    `onConnectionDead` → grant re-exchange), never fall back to tokenless
 *    pairing mode and wait for a manual device approval.
 */
export function withCloudConnection(
  base: TerminalConfig,
  opts: CloudConnectionOptions,
): TerminalConfig {
  return {
    ...base,
    gatewayUrl: opts.grant.gatewayUrl,
    fallbackPairingToken: opts.grant.token,
    resolveHandshakeToken: opts.grant.usesDimeJwt
      ? opts.resolveHandshakeToken
      : undefined,
    ephemeralConnection: true,
    supportsPairingRole: false,
    sidecar: opts.sidecar,
    credentials: opts.credentials,
    onConnectionDebug: (event, detail) => {
      base.onConnectionDebug?.(event, detail);
      if (event === 'status' && detail?.to === 'error') {
        opts.onConnectionDead();
      }
    },
  };
}
